/**
 * Dashboard — authenticated user's home. Uses Card, Badge, EmptyState, Skeleton.
 */

import { Link } from 'react-router-dom';
import {
  Trophy,
  Calendar,
  Users,
  Target,
  ArrowRight,
  Clock,
  Sparkles,
} from 'lucide-react';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/card';
import Badge from '../components/ui/badge';
import Skeleton from '../components/ui/skeleton';
import { useAppStore } from '../stores/appStore';
import { usePools } from '../hooks/usePools';
import { useUpcomingMatches } from '../hooks/useMatches';
import { formatMatchDate, formatTime, cn } from '../lib/utils';
import type { Match, Pool } from '../../shared/types';
import { useSEO, SEO_CONFIGS } from '../hooks/useSEO';

export default function DashboardPage() {
  useSEO(SEO_CONFIGS.dashboard);

  const user = useAppStore((state) => state.user);
  const { data: pools, isLoading: poolsLoading } = usePools();
  const { data: upcomingMatches, isLoading: matchesLoading } = useUpcomingMatches();

  const firstName = user?.name?.split(' ')[0];

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Welcome / hero */}
      <section
        className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 dark:from-primary-600 dark:to-primary-700 p-6 sm:p-8 text-white"
        aria-labelledby="dashboard-welcome-heading"
      >
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute top-0 right-0 h-64 w-64 rounded-full bg-secondary-400/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-accent-400/20 blur-3xl" />
        </div>
        <div className="relative">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur-sm">
            <Sparkles className="h-3.5 w-3.5 text-secondary-300" aria-hidden="true" />
            Copa do Mundo 2026
          </div>
          <h1
            id="dashboard-welcome-heading"
            className="mb-2 text-2xl sm:text-3xl font-bold tracking-tight"
          >
            Olá, {firstName ?? 'jogador'}!
          </h1>
          <p className="text-white/85 max-w-md text-[15px]">
            Faça seus palpites, acompanhe o ranking e dispute pontos com seus amigos.
          </p>
        </div>
      </section>

      {/* Quick stats */}
      <section
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Resumo do bolão"
      >
        <StatCard
          icon={Users}
          label="Meus bolões"
          value={pools?.length ?? 0}
          tone="primary"
        />
        <StatCard
          icon={Calendar}
          label="Próximos jogos"
          value={upcomingMatches?.length ?? 0}
          tone="secondary"
        />
        <StatCard icon={Target} label="Palpites feitos" value="—" tone="info" />
        <StatCard icon={Trophy} label="Melhor posição" value="—" tone="success" />
      </section>

      {/* Two-column: upcoming matches + my pools */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-foreground">
              <Clock className="h-4 w-4 text-primary-500 dark:text-primary-400" aria-hidden="true" />
              Próximos jogos
            </h2>
            <Link
              to="/jogos"
              className="text-sm font-medium text-primary-700 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              Ver todos
            </Link>
          </div>

          {matchesLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))}
            </div>
          ) : upcomingMatches?.length ? (
            <div className="space-y-3">
              {upcomingMatches.slice(0, 4).map((match: Match, index: number) => (
                <MatchTile key={match.id} match={match} index={index} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700 py-8 text-center">
              <Calendar className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden="true" />
              <p className="mt-2 text-sm text-muted-foreground">Nenhum jogo agendado</p>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="inline-flex items-center gap-2 text-lg font-semibold text-foreground">
              <Trophy className="h-4 w-4 text-primary-500 dark:text-primary-400" aria-hidden="true" />
              Meus bolões
            </h2>
            <Link
              to="/boloes"
              className="text-sm font-medium text-primary-700 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              Ver todos
            </Link>
          </div>

          {poolsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 rounded-xl" />
              ))}
            </div>
          ) : pools?.length ? (
            <div className="space-y-3">
              {pools.slice(0, 4).map((pool: Pool, index: number) => (
                <PoolTile key={pool.id} pool={pool} index={index} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700 py-8 text-center">
              <Users className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden="true" />
              <p className="mt-2 mb-4 text-sm text-muted-foreground">
                Você ainda não participa de nenhum bolão
              </p>
              <Link to="/boloes">
                <Button size="sm">Explorar bolões</Button>
              </Link>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const TONE_STYLES = {
  primary: 'bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300',
  secondary:
    'bg-secondary-100 dark:bg-secondary-500/20 text-secondary-700 dark:text-secondary-400',
  info: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
  success: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300',
} as const;

function StatCard({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  tone: keyof typeof TONE_STYLES;
}) {
  return (
    <Card className="p-5 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
      <div className={cn('mb-3 flex h-10 w-10 items-center justify-center rounded-xl', TONE_STYLES[tone])}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>
      <p className="text-2xl font-bold text-foreground tabular-nums">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </Card>
  );
}

function MatchTile({ match, index }: { match: Match; index: number }) {
  return (
    <Link
      to={`/jogos/${match.id}`}
      style={{ animationDelay: `${Math.min(index * 50, 200)}ms` }}
      className={cn(
        'group block rounded-xl border border-transparent p-4 transition-all duration-200 animate-fade-in',
        'bg-neutral-50 dark:bg-neutral-800/40',
        'hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:border-neutral-200 dark:hover:border-neutral-700',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
            <span>{formatMatchDate(match.matchDate)}</span>
            <span aria-hidden="true">•</span>
            <span className="tabular-nums">{formatTime(match.matchDate)}</span>
          </div>
          <div className="font-medium text-foreground truncate">
            {match.homeTeam} × {match.awayTeam}
          </div>
        </div>
        <ArrowRight
          className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500"
          aria-hidden="true"
        />
      </div>
    </Link>
  );
}

function PoolTile({ pool, index }: { pool: Pool; index: number }) {
  return (
    <Link
      to={`/boloes/${pool.slug}`}
      style={{ animationDelay: `${Math.min(index * 50, 200)}ms` }}
      className={cn(
        'group block rounded-xl border border-transparent p-4 transition-all duration-200 animate-fade-in',
        'bg-neutral-50 dark:bg-neutral-800/40',
        'hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:border-neutral-200 dark:hover:border-neutral-700',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-0.5 flex items-center gap-2">
            {pool.isDemo && <Badge variant="warning">Demo</Badge>}
            <span className="font-medium text-foreground truncate">{pool.name}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            {pool.memberCount || 0}{' '}
            {pool.memberCount === 1 ? 'participante' : 'participantes'}
          </div>
        </div>
        <ArrowRight
          className="h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary-500"
          aria-hidden="true"
        />
      </div>
    </Link>
  );
}
